/**
 * Lokale (geräte-eigene) Panel-Ansichts-Einstellungen: Ansichtsvariante und
 * persönliche Tastenreihenfolge. Bewusst NICHT im Backend gespeichert — das ist
 * eine reine Anzeige-Präferenz DIESES Geräts und ändert nichts für andere.
 *
 * Pendant in iOS (UserDefaults) und Android (SharedPreferences).
 */
export type PanelViewMode = 'list' | 'grid';

export interface PanelPrefs {
  /** 'list' = normale volle Zeilen (Standard), 'grid' = kompakte Kästchen (4 nebeneinander). */
  viewMode: PanelViewMode;
  /** Persönliche Reihenfolge als Permutation der Slot-Positionen. Leer = Original-Layout. */
  order: number[];
}

const KEY = (panelId: string): string => `livepuls.panelview.${panelId}`;

export function loadPanelPrefs(panelId: string): PanelPrefs {
  const fallback: PanelPrefs = { viewMode: 'list', order: [] };
  try {
    const raw = localStorage.getItem(KEY(panelId));
    if (!raw) return fallback;
    const p = JSON.parse(raw) as Partial<PanelPrefs>;
    return {
      viewMode: p.viewMode === 'grid' ? 'grid' : 'list',
      order: Array.isArray(p.order) ? p.order.filter((n) => typeof n === 'number') : [],
    };
  } catch {
    return fallback;
  }
}

export function savePanelPrefs(panelId: string, prefs: PanelPrefs): void {
  try {
    localStorage.setItem(KEY(panelId), JSON.stringify(prefs));
  } catch {
    /* Speicher voll / privater Modus → Präferenz bleibt eben nur für die Sitzung. */
  }
}

/**
 * Wendet die persönliche Reihenfolge auf die Slots an: erst die gespeicherten
 * Positionen (soweie sie noch existieren), dann alle neuen Slots in Original-
 * Reihenfolge angehängt. So überleben Layout-Änderungen aus dem Admin.
 */
export function applyOrder<T extends { position: number }>(slots: T[], order: number[]): T[] {
  const byPos = new Map(slots.map((s) => [s.position, s]));
  const seen = new Set<number>();
  const out: T[] = [];
  for (const pos of order) {
    const s = byPos.get(pos);
    if (s && !seen.has(pos)) {
      out.push(s);
      seen.add(pos);
    }
  }
  for (const s of slots) {
    if (!seen.has(s.position)) out.push(s);
  }
  return out;
}
